/**
 * External Sync Persistence Helper
 * 
 * Handles persisting sync results to the database after external calendar
 * sync operations complete. Integrates with CalendarEventService.
 * 
 * @module calendar/integrations/persistExternalSync
 * @version 1.0.0
 */

import { CalendarEventService } from '../services/CalendarEventService';
import type { ExternalSyncInfo } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extra source data to persist along with sync info.
 * Used for Teams meeting URLs, etc.
 */
export interface ExtraSourceData {
  /** Teams meeting join URL */
  teamsMeetingUrl?: string;
  /** Any additional provider-specific data */
  [key: string]: unknown;
}

/**
 * Result of a persist operation.
 */
export interface PersistSyncResult {
  /** Whether persist succeeded */
  success: boolean;
  /** Whether this was an update to existing sync (vs new) */
  wasUpdate: boolean;
  /** Error message if failed */
  error?: string;
  /** Timestamp of persist attempt */
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sync Log (in-memory for error tracking)
// ─────────────────────────────────────────────────────────────────────────────

interface PersistLogEntry {
  timestamp: string;
  ax1EventId: string;
  provider: 'google' | 'microsoft';
  externalEventId: string;
  operation: 'created' | 'updated' | 'error';
  error?: string;
}

const persistLog: PersistLogEntry[] = [];
const MAX_LOG_ENTRIES = 100;

function logPersist(entry: Omit<PersistLogEntry, 'timestamp'>): void {
  persistLog.unshift({
    ...entry,
    timestamp: new Date().toISOString(),
  });
  
  // Trim log to max size
  if (persistLog.length > MAX_LOG_ENTRIES) {
    persistLog.length = MAX_LOG_ENTRIES;
  }
}

/**
 * Get recent persist log entries.
 */
export function getPersistSyncLog(limit = 50): PersistLogEntry[] {
  return persistLog.slice(0, limit);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Persists external sync info to the database for an AX1 event.
 * 
 * Behavior:
 * - If event has no existing sync for this provider/externalEventId: creates sync link
 * - If event already has sync with same externalEventId: updates lastSyncedAt only (idempotent)
 * - Stores extraSourceData (e.g., teamsMeetingUrl) in externalSourceData field
 * - Failures are logged but don't crash - returns result object
 * 
 * @param ax1EventId - The AX1 calendar event ID
 * @param syncInfo - External sync metadata to persist
 * @param extraSourceData - Optional extra data (Teams URL, etc.)
 * @returns Persist result with success/error info
 * 
 * @example
 * // After pushing to Google
 * const result = await pushAx1EventToGoogle(event);
 * if (result.success && result.data?.syncInfo) {
 *   await persistExternalSync(event.id, result.data.syncInfo);
 * }
 * 
 * @example
 * // After creating Outlook meeting with Teams
 * const result = await createOutlookMeeting(event, true);
 * if (result.success && result.data?.syncInfo) {
 *   await persistExternalSync(event.id, result.data.syncInfo, {
 *     teamsMeetingUrl: result.data.teamsMeetingUrl
 *   });
 * }
 */
export async function persistExternalSync(
  ax1EventId: string,
  syncInfo: ExternalSyncInfo,
  extraSourceData?: ExtraSourceData
): Promise<PersistSyncResult> {
  const timestamp = new Date().toISOString();
  
  // Validate inputs
  if (!ax1EventId) {
    const error = 'Missing ax1EventId';
    logPersist({
      ax1EventId: 'unknown',
      provider: syncInfo?.provider ?? 'google',
      externalEventId: syncInfo?.externalEventId ?? 'unknown',
      operation: 'error',
      error,
    });
    return { success: false, wasUpdate: false, error, timestamp };
  }
  
  if (!syncInfo?.provider || !syncInfo?.externalEventId) {
    const error = 'Invalid syncInfo: missing provider or externalEventId';
    logPersist({
      ax1EventId,
      provider: syncInfo?.provider ?? 'google',
      externalEventId: syncInfo?.externalEventId ?? 'unknown',
      operation: 'error',
      error,
    });
    return { success: false, wasUpdate: false, error, timestamp };
  }

  try {
    const service = CalendarEventService.getInstance();
    
    // First, check if this event already has sync info for the same external event
    const existingResult = await service.getEventById(ax1EventId);
    
    if (!existingResult.success) {
      // Event doesn't exist or couldn't be fetched
      const error = existingResult.error?.message ?? 'Failed to fetch event';
      logPersist({
        ax1EventId,
        provider: syncInfo.provider,
        externalEventId: syncInfo.externalEventId,
        operation: 'error',
        error,
      });
      return { success: false, wasUpdate: false, error, timestamp };
    }

    const existingEvent = existingResult.data;
    const existingSync = existingEvent?.externalSync;
    
    // Check if this is an update to existing sync (idempotent case)
    const isUpdate = existingSync?.provider === syncInfo.provider &&
                     existingSync?.externalEventId === syncInfo.externalEventId;
    
    // Build the sync info to persist
    let finalSyncInfo: ExternalSyncInfo;
    
    if (isUpdate) {
      // Idempotent update: preserve existing fields, only update lastSyncedAt and status
      finalSyncInfo = {
        ...existingSync,
        lastSyncedAt: syncInfo.lastSyncedAt ?? new Date().toISOString(),
        syncStatus: 'synced',
        syncError: undefined, // Clear any previous error
        // Preserve external last modified if provided
        externalLastModified: syncInfo.externalLastModified ?? existingSync.externalLastModified,
      };
    } else {
      // New sync link
      finalSyncInfo = {
        ...syncInfo,
        syncStatus: 'synced',
        syncError: undefined,
      };
    }

    // Build externalSourceData with Teams URL if provided
    let finalSourceData: Record<string, unknown> | undefined;
    
    if (extraSourceData && Object.keys(extraSourceData).length > 0) {
      // Merge with existing source data if present
      finalSourceData = {
        ...(existingEvent?.externalSourceData ?? {}),
        ...extraSourceData,
      };
    } else if (existingEvent?.externalSourceData) {
      // Preserve existing source data
      finalSourceData = existingEvent.externalSourceData;
    }

    // Persist to database
    const updateResult = await service.updateEvent(ax1EventId, {
      externalSync: finalSyncInfo,
      ...(finalSourceData ? { externalSourceData: finalSourceData } : {}),
    });

    if (!updateResult.success) {
      const error = updateResult.error?.message ?? 'Failed to update event';
      logPersist({
        ax1EventId,
        provider: syncInfo.provider,
        externalEventId: syncInfo.externalEventId,
        operation: 'error',
        error,
      });
      return { success: false, wasUpdate: isUpdate, error, timestamp };
    }

    // Success - log and return
    logPersist({
      ax1EventId,
      provider: syncInfo.provider,
      externalEventId: syncInfo.externalEventId,
      operation: isUpdate ? 'updated' : 'created',
    });

    return {
      success: true,
      wasUpdate: isUpdate,
      timestamp,
    };
    
  } catch (err) {
    // Unexpected error - log but don't crash
    const error = err instanceof Error ? err.message : 'Unknown error during persist';
    
    logPersist({
      ax1EventId,
      provider: syncInfo.provider,
      externalEventId: syncInfo.externalEventId,
      operation: 'error',
      error,
    });
    
    // Log to console for debugging but don't throw
    console.error('[persistExternalSync] Failed to persist sync info:', {
      ax1EventId,
      provider: syncInfo.provider,
      externalEventId: syncInfo.externalEventId,
      error,
    });
    
    return { success: false, wasUpdate: false, error, timestamp };
  }
}

/**
 * Batch persist multiple sync results.
 * Useful after batch sync operations.
 * 
 * @param results - Array of sync results to persist
 * @returns Summary of persist operations
 */
export async function persistExternalSyncBatch(
  results: Array<{
    ax1EventId: string;
    syncInfo: ExternalSyncInfo;
    extraSourceData?: ExtraSourceData;
  }>
): Promise<{
  total: number;
  succeeded: number;
  failed: number;
  results: PersistSyncResult[];
}> {
  const persistResults: PersistSyncResult[] = [];
  let succeeded = 0;
  let failed = 0;

  for (const item of results) {
    const result = await persistExternalSync(
      item.ax1EventId,
      item.syncInfo,
      item.extraSourceData
    );
    
    persistResults.push(result);
    
    if (result.success) {
      succeeded++;
    } else {
      failed++;
    }
  }

  return {
    total: results.length,
    succeeded,
    failed,
    results: persistResults,
  };
}

export default persistExternalSync;
